import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Accordion } from "@/shared/ui/accordion";
import { Card } from "@/shared/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { cn } from "@/shared/lib/utils";
import { StatusBanner } from "../StatusBanner";
import { DURATION, EASE, SECTION_REVEAL, SECTION_STAGGER } from "../motion";
import { DockedComposerBar } from "../dashboardSurface/DockedComposerBar";
import { EmptyState } from "../dashboardSurface/EmptyState";
import { AnswerSection } from "../sections/AnswerSection";
import { KpiRow } from "../sections/KpiRow";
import { SectionCallout } from "../sections/SectionCallout";
import { ChatTurnItem } from "./ChatTurnItem";
import { displayCell, isDisplayNumericColumn, ResultSetCard } from "./ResultSetCard";
import { isNumericColumn, resolveChartRef } from "./chartRef";
import type { Followup, KpiItem, PhaseName } from "../types";
import type { IntelGate } from "@/features/intel-layer/hooks/useIntelGate";
import {
  streamIntelChat,
  type IntelChartProposal,
  type IntelDiagnostics,
  type IntelDroppedSetStub,
  type IntelFinalMeta,
  type IntelResultSet,
  type IntelTableView,
  type IntelWireBlock,
} from "./streamIntelChat";

/** The fixed tenant-neutral chip set — BOTH the seed chips and the post-answer
 *  followups, never dynamic (spec §5). Kinds are presentation only (icon/tag). */
export const INTEL_CHAT_CHIPS: Followup[] = [
  { text: "Give me an overview of the portfolio", kind: "drill" },
  { text: "Compare stores by survey completion", kind: "compare" },
  { text: "Show me incomplete nameplate data", kind: "pivot" },
  { text: "Which stores are missing photos?", kind: "verify" },
];

export type IntelTurn = {
  id: string;
  question: string;
  status: "running" | "done" | "error";
  /** Latest progress-frame label — the only pre-final signal. */
  progressLabel: string | null;
  answer: string;
  finalMeta: IntelFinalMeta | null;
  resultSets: IntelResultSet[];
  /** Sets the server dropped past the wire byte budget (ticket-29 servers). */
  setsDropped: number;
  errorMessage?: string;
  /** Submit timestamp — drives the StatusBanner elapsed timer. */
  startedAt: number;
  /** Set when the run settles (done/error) so the elapsed label freezes. */
  finishedAt: number | null;
  /** Last stream-callback timestamp — feeds the banner's stall watchdog. */
  lastFrameTs: number | null;
  /** First time the phase mapper yields "querying" — the banner's
   *  tool→result watchdog anchor. */
  lastToolUsedTs: number | null;
  /** Contract-v2 rendered blocks from the `answer_blocks` frame (ticket 38):
   *  null until the frame arrives — and forever on v1 wires, which keep the
   *  markdown text path. */
  answerBlocks: IntelWireBlock[] | null;
  /** Contract-v2 assembly-honesty frame; null on v1 wires. */
  diagnostics: IntelDiagnostics | null;
  /** Budget-dropped set stubs off the result_sets frame (v2 wires only). */
  droppedSets: IntelDroppedSetStub[];
};

/** Deterministic phase mapper for the StatusBanner run card (ruling 40 R2):
 *  intel-chat has no phase frames, only progress labels, so the banner's
 *  canonical phase is derived from the latest label's wording. */
function phaseForProgressLabel(label: string | null): PhaseName {
  if (label && /quer|sql|template|database|running/i.test(label)) return "querying";
  if (label && /assembl|build|writ|final|answer/i.test(label)) return "building";
  return "thinking";
}

interface IntelChatProps {
  clientId: string;
  clientName: string;
  /** Required (no fail-open default): useIntelGate is mandatory on this
   *  surface — intel-chat deliberately does not consult the RPC gate. */
  chatGate: IntelGate;
}

function newTurnId(): string {
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Layer 2 — every submitted question (typed or chip) runs one independent
 * intel-chat turn rendered as an accordion panel in the DashboardSurface shell
 * anatomy (ruling 40 R2: EmptyState landing, question-in-header panels,
 * StatusBanner run card, scroll-revealed docked composer). The answer renders
 * inline by fixed rules (contract §10 + ruling 40): answer text → sectioned
 * chips (headings group content, paragraph chips reorder above their tables);
 * result sets → verbatim tables (emit-all: cited expanded, uncited collapsed,
 * first 2 expanded when nothing is cited), except 1×1 sets which render as
 * KPI tiles (R5 floor). Under contract v2 (ticket 38) the answer half renders
 * from the `answer_blocks` frame instead — claims as chips, caveats as fixed
 * amber callouts, table/chart refs as inline mounts — with the same emit-all
 * audit stack below. Nothing else — no chart inference, no evidence-selection
 * heuristics.
 */
export function IntelChat({ clientId, clientName, chatGate }: IntelChatProps) {
  const [turns, setTurns] = useState<IntelTurn[]>([]);
  const [draft, setDraft] = useState("");
  // Controlled accordion: on submit the new turn's panel expands and all
  // others collapse; manual toggles are respected afterwards (DashboardSurface
  // expansion policy minus pin — pin is dropped by ruling).
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  // Scroll-reveal state for the docked composer (see recomputeBarReveal).
  const [barRevealed, setBarRevealed] = useState(true);
  // 200ms wall-clock tick while streaming so the banner's elapsed label stays
  // smooth between SSE frames (useDashboardStream's local-ticker pattern).
  const [nowMs, setNowMs] = useState(() => Date.now());
  const abortRef = useRef<AbortController | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const revealRafRef = useRef<number | null>(null);

  const streaming = turns[turns.length - 1]?.status === "running";

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [turns.length]);

  useEffect(() => {
    if (!streaming) return;
    const t = setInterval(() => setNowMs(Date.now()), 200);
    return () => clearInterval(t);
  }, [streaming]);

  const patchTurn = useCallback((id: string, patch: Partial<IntelTurn>) => {
    setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  /** Patch + stamp lastFrameTs — every stream callback feeds the banner's
   *  stall watchdog. */
  const patchTurnOnFrame = useCallback((id: string, patch: Partial<IntelTurn>) => {
    const now = Date.now();
    setTurns((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...patch, lastFrameTs: now } : t)),
    );
  }, []);

  const submitQuestion = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || chatGate !== "open") return;
      if (abortRef.current) return; // one run at a time

      const id = newTurnId();
      setTurns((prev) => [
        ...prev,
        {
          id,
          question: q,
          status: "running",
          progressLabel: null,
          answer: "",
          finalMeta: null,
          resultSets: [],
          setsDropped: 0,
          startedAt: Date.now(),
          finishedAt: null,
          lastFrameTs: null,
          lastToolUsedTs: null,
          answerBlocks: null,
          diagnostics: null,
          droppedSets: [],
        },
      ]);
      // New question: its panel expands, all others collapse.
      setExpandedIds([id]);
      setDraft("");

      const controller = new AbortController();
      abortRef.current = controller;
      try {
        await streamIntelChat({
          clientId,
          question: q,
          signal: controller.signal,
          handlers: {
            onProgress: (label) => {
              const now = Date.now();
              setTurns((prev) =>
                prev.map((t) =>
                  t.id === id
                    ? {
                        ...t,
                        progressLabel: label,
                        lastFrameTs: now,
                        // First "querying" label = the banner's tool anchor.
                        lastToolUsedTs:
                          t.lastToolUsedTs ??
                          (phaseForProgressLabel(label) === "querying" ? now : null),
                      }
                    : t,
                ),
              );
            },
            onAnswerText: (text) => patchTurnOnFrame(id, { answer: text }),
            onFinalMeta: (meta) => patchTurnOnFrame(id, { finalMeta: meta }),
            onResultSets: (sets, info) =>
              patchTurnOnFrame(id, {
                resultSets: sets,
                setsDropped: info.setsDropped,
                droppedSets: info.droppedSets ?? [],
              }),
            onAnswerBlocks: (blocks) => patchTurnOnFrame(id, { answerBlocks: blocks }),
            onDiagnostics: (diagnostics) => patchTurnOnFrame(id, { diagnostics }),
          },
        });
        patchTurn(id, { status: "done", progressLabel: null, finishedAt: Date.now() });
      } catch (error) {
        if (controller.signal.aborted) {
          // Stopped by the user: keep whatever already arrived.
          const finishedAt = Date.now();
          setTurns((prev) =>
            prev.map((t) =>
              t.id === id
                ? t.answer
                  ? { ...t, status: "done", progressLabel: null, finishedAt }
                  : {
                      ...t,
                      status: "error",
                      progressLabel: null,
                      finishedAt,
                      errorMessage: "Stopped.",
                    }
                : t,
            ),
          );
        } else {
          patchTurn(id, {
            status: "error",
            progressLabel: null,
            finishedAt: Date.now(),
            errorMessage:
              error instanceof Error ? error.message : "Something went wrong — please try again.",
          });
        }
      } finally {
        abortRef.current = null;
      }
    },
    [chatGate, clientId, patchTurn, patchTurnOnFrame],
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (streaming) return;
    void submitQuestion(draft);
  };

  const handleStop = () => abortRef.current?.abort();

  // Reveal the docked composer + chips only once the reader has scrolled near
  // the end of the content, or when the content fits without scrolling — so
  // the bar never obfuscates the answer mid-read (DashboardSurface behavior).
  const recomputeBarReveal = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const scrollable = el.scrollHeight > el.clientHeight + 8;
    setBarRevealed(!scrollable || distanceFromBottom < 140);
  }, []);

  const handleScroll = useCallback(() => {
    if (revealRafRef.current != null) return;
    revealRafRef.current = requestAnimationFrame(() => {
      revealRafRef.current = null;
      recomputeBarReveal();
    });
  }, [recomputeBarReveal]);

  // Re-evaluate reveal when the conversation changes (a new answer lands, a
  // stream ends) — those shift scroll geometry without firing a scroll event.
  useEffect(() => {
    const id = requestAnimationFrame(recomputeBarReveal);
    return () => cancelAnimationFrame(id);
  }, [turns.length, streaming, recomputeBarReveal]);

  // Clear any in-flight scroll-reveal frame on unmount so it doesn't fire a
  // setState after the surface is gone.
  useEffect(
    () => () => {
      if (revealRafRef.current != null) cancelAnimationFrame(revealRafRef.current);
    },
    [],
  );

  // Bar always shown while streaming (parity with DashboardSurface — the mount
  // gate below hides it entirely mid-stream; Stop lives in the StatusBanner);
  // otherwise it follows scroll-reveal.
  const barShown = streaming || barRevealed;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        <div className="mx-auto max-w-6xl px-6 py-6 pb-8">
          <AnimatePresence>
            {turns.length === 0 ? (
              // Empty-state agent surface — greeting + composer + starter
              // chips (EmptyState owns the chatGate branches). Exits with the
              // DashboardSurface soft dissolve so the swap into the first turn
              // reads as one motion.
              <motion.div
                key="empty-state"
                exit={{ opacity: 0, y: -12, scale: 0.98 }}
                transition={{ duration: DURATION.short, ease: EASE.in }}
                className="flex min-h-[70vh] flex-col items-center justify-center text-center"
              >
                <EmptyState
                  inputRef={inputRef}
                  clientName={clientName}
                  draft={draft}
                  streaming={streaming}
                  chatGate={chatGate}
                  onDraftChange={setDraft}
                  onSubmit={handleSubmit}
                  onStop={handleStop}
                  onPickFollowup={(q) => void submitQuestion(q)}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>

          <Accordion
            type="multiple"
            value={expandedIds}
            onValueChange={setExpandedIds}
            className="space-y-2"
          >
            {turns.map((turn) => (
              <ChatTurnItem
                key={turn.id}
                id={turn.id}
                question={turn.question}
                running={turn.status === "running"}
              >
                <TurnStatusBanner turn={turn} nowMs={nowMs} onStop={handleStop} />
                <AssistantTurn turn={turn} />
              </ChatTurnItem>
            ))}
          </Accordion>
          <div ref={endRef} />
        </div>

        {/* Sticky-bottom composer + follow-up chips. Scroll-revealed, and
            hidden entirely while streaming — Stop lives in the StatusBanner
            run card instead (DashboardSurface shell behavior). */}
        {turns.length > 0 && !streaming ? (
          <DockedComposerBar
            ref={inputRef}
            barShown={barShown}
            streaming={streaming}
            followups={INTEL_CHAT_CHIPS}
            draft={draft}
            clientName={clientName}
            onDraftChange={setDraft}
            onSubmit={handleSubmit}
            onStop={handleStop}
            onPickFollowup={(q) => void submitQuestion(q)}
            onFocusReveal={() => setBarRevealed(true)}
          />
        ) : null}
      </div>
    </div>
  );
}

/** The LivePanel run card, fed from the intel stream (no phase frames): the
 *  phase is mapped deterministically from the latest progress label; on done
 *  the banner stays mounted in its muted "Built in N.Ns" complete state (the
 *  turn panel is both the live panel and the committed record); on error it
 *  hides (phase null + not streaming → StatusBanner renders nothing) and the
 *  risk callout in the turn body takes over. */
function TurnStatusBanner({
  turn,
  nowMs,
  onStop,
}: {
  turn: IntelTurn;
  nowMs: number;
  onStop: () => void;
}) {
  const running = turn.status === "running";
  const phase: PhaseName | null =
    turn.status === "error"
      ? null
      : running
        ? phaseForProgressLabel(turn.progressLabel)
        : "complete";
  const elapsedMs = Math.max(0, (turn.finishedAt ?? nowMs) - turn.startedAt);
  return (
    <StatusBanner
      phase={phase}
      phaseLabel={turn.progressLabel}
      elapsedMs={elapsedMs}
      lastFrameTs={turn.lastFrameTs}
      lastToolUsedTs={turn.lastToolUsedTs}
      streaming={running}
      question={turn.question}
      onStop={onStop}
    />
  );
}

/** Emit-all wire contract (intel ticket 25): `cited` marks provenance-cited
 *  sets. Pre-emit-all servers omit the flag and emit only cited sets, so a
 *  missing flag means cited. */
function isCitedSet(set: IntelResultSet): boolean {
  return set.cited !== false;
}

/** Ticket-31 wire contract (pinned in intel map issue 31): the engine writes a
 *  deterministic marker into the answer TEXT wherever the model placed a
 *  {"kind":"table","qtag"} segment. The client splits on it and mounts the
 *  real table inline — the model composes prose + engine-owned tables. */
const TABLE_REF_MARKER_RE = /\[\[table:(q\d+_\d+)\]\]/g;

type AnswerChunk = { kind: "prose"; text: string } | { kind: "table"; qtag: string };

export function splitAnswerOnTableRefs(answer: string): AnswerChunk[] {
  const chunks: AnswerChunk[] = [];
  let last = 0;
  for (const m of answer.matchAll(TABLE_REF_MARKER_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) chunks.push({ kind: "prose", text: answer.slice(last, idx) });
    chunks.push({ kind: "table", qtag: m[1] });
    last = idx + m[0].length;
  }
  if (last < answer.length) chunks.push({ kind: "prose", text: answer.slice(last) });
  return chunks;
}

/** Ticket 45 round 2 — the DashboardRenderer staggered-entrance pattern lifted
 *  for answer content: each block fades/slides in (SECTION_REVEAL) under a
 *  staggering parent, so blocks reveal in sequence on done-turn mount and
 *  individually as they arrive mid-stream. MotionConfig reducedMotion="user"
 *  wraps the surface, so these entrances self-disable under the OS preference. */
function RevealBlock({ children }: { children: ReactNode }) {
  return (
    <motion.div
      variants={SECTION_REVEAL}
      initial="hidden"
      animate="show"
      transition={{ duration: DURATION.short, ease: EASE.out }}
    >
      {children}
    </motion.div>
  );
}

/** Ticket 45 round 2 — the DashboardRenderer "writing" indicator lifted
 *  verbatim: three dots bobbing at the bottom of a running turn's body while
 *  content is still arriving; static 0.6-opacity dots under reduced motion.
 *  The StatusBanner carries the announced progress, so this is aria-hidden. */
function StreamingDots() {
  const reduce = useReducedMotion();
  return (
    <div
      data-testid="streaming-dots"
      className="flex items-center gap-1.5 px-1 py-2"
      aria-hidden
    >
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/50"
          animate={reduce ? { opacity: 0.6 } : { opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
          transition={
            reduce
              ? { duration: 0 }
              : { duration: 0.9, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }
          }
        />
      ))}
    </div>
  );
}

/** Ruling 40 R5 — the deterministic KPI-tile floor. Shape-only: a 1-row ×
 *  1-column set never renders as a table card; it renders as a KPI tile
 *  REPLACING the card, in both the supporting stack and inline table-ref
 *  mounts. 1×N / N×1 with N>1 stay tables. Internal (working-query) sets are
 *  exempt — they stay collapsed debug cards even at 1×1. */
function isOneByOneSet(set: IntelResultSet): boolean {
  return set.rows.length === 1 && set.columns.length === 1;
}

/** Tile label = the card title chain minus the ordinal fallback. */
function kpiItemForSet(set: IntelResultSet): KpiItem {
  return {
    label: set.label?.trim() || set.columns[0].replace(/_/g, " "),
    value: String(displayCell(set.rows[0][0])),
  };
}

/** One coalesced strip of adjacent 1×1 sets in the General-dashboard kpi_row
 *  idiom. Judgment calls: a single tile is width-constrained (a lone tile in
 *  a full-width card reads as a broken table), and tiles carry no cited badge
 *  (it doesn't fit the tile visual). */
function KpiStrip({ sets }: { sets: IntelResultSet[] }) {
  return (
    <Card
      data-testid="kpi-strip"
      className={cn("overflow-hidden", sets.length === 1 && "max-w-xs")}
    >
      <KpiRow items={sets.map(kpiItemForSet)} />
    </Card>
  );
}

/** Ticket 45 "BUILD at fold-in" — the 1×(2–8) KPI-strip floor: one row whose
 *  2–8 columns are ALL numeric values (columns_meta when present, else the
 *  cell heuristic minus identifier-named columns) renders as a bare KPI strip
 *  replacing the table card, in the stack AND inline mounts. Applies on v1
 *  wires too — a deliberate flag-off visual change, ratified at 45. Null when
 *  the shape doesn't qualify. */
function kpiFloorItems(set: IntelResultSet): KpiItem[] | null {
  if (set.rows.length !== 1) return null;
  if (set.columns.length < 2 || set.columns.length > 8) return null;
  for (let i = 0; i < set.columns.length; i += 1) {
    if (!isDisplayNumericColumn(set, i)) return null;
  }
  return set.columns.map((column, i) => ({
    label: column.replace(/_/g, " "),
    value: String(displayCell(set.rows[0][i])),
  }));
}

/** The 1×(2–8) floor render — one whole set as a KPI strip (KpiStrip chrome,
 *  one item per COLUMN instead of per 1×1 set). */
function KpiFloorStrip({ items }: { items: KpiItem[] }) {
  return (
    <Card data-testid="kpi-floor-strip" className="overflow-hidden">
      <KpiRow items={items} />
    </Card>
  );
}

/** Audit grouping (35 Q4): the contract-v2 `role` decides when present
 *  (work|debug soft-hide behind the working-queries line), else the v1
 *  `internal` bridge flag. */
function isWorkSet(set: IntelResultSet): boolean {
  return set.role != null ? set.role === "work" || set.role === "debug" : set.internal === true;
}

/** Apply a server-VALIDATED table view (column subset, sort, limit) to derive
 *  the view-shaped inline display set (ticket 38). Presentation-only — the
 *  ORIGINAL set still renders in the audit stack; the view_note stamp
 *  promises it. */
function applyTableView(set: IntelResultSet, view: IntelTableView): IntelResultSet {
  const columnIdx =
    view.columns && view.columns.length > 0
      ? view.columns.map((name) => set.columns.indexOf(name)).filter((i) => i !== -1)
      : set.columns.map((_c, i) => i);
  let rows = set.rows;
  if (view.sort) {
    const si = set.columns.indexOf(view.sort.by);
    if (si !== -1) {
      const dir = view.sort.dir === "desc" ? -1 : 1;
      const numeric = isNumericColumn(set, si);
      rows = [...rows].sort((a, b) => {
        const av = a[si];
        const bv = b[si];
        // Nulls sink to the end regardless of direction (presentation rule,
        // same spirit as the all-null column bucket).
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = numeric
          ? Number(av) - Number(bv)
          : String(av).localeCompare(String(bv));
        return cmp * dir;
      });
    }
  }
  const limited = view.limit != null ? rows.slice(0, view.limit) : rows;
  const keptNames = new Set(columnIdx.map((i) => set.columns[i]));
  return {
    ...set,
    columns: columnIdx.map((i) => set.columns[i]),
    columns_meta: set.columns_meta?.filter((m) => keptNames.has(m.name)),
    rows: limited.map((row) => columnIdx.map((i) => row[i])),
    shown_row_count: limited.length,
    // When a view limit trimmed rows the view_note stamp carries the
    // "showing N of M" honesty — don't double-report via the truncation meta.
    truncated: limited.length === set.rows.length ? set.truncated : false,
  };
}

// ---- Contract v2 (ticket 38): the wire-block render plan --------------------
// Deterministic pipeline over the answer_blocks frame:
//   (a) a claim immediately following a table_ref whose qtag is in the claim's
//       evidence becomes that mount's attached insight callout (46 adjacency
//       rule), not a chip;
//   (b) remaining blocks stable-partition prose-first (claims + caveats in
//       model order, then table_ref/chart_ref in model order — the R4a
//       claims-before-tables doctrine, applied client-side);
//   (c) refs dedupe by qtag (first mounts, repeats render nothing); adjacent
//       1×1 refs coalesce into one KPI strip; the 1×1 tile and 1×(2–8) strip
//       floors beat every block kind INCLUDING valid chart proposals (44 R11;
//       the 45 rider defers any model say over the floors to post-deploy);
//   (d) full-set inline mounts and chart mounts dedupe their set out of the
//       audit stack; view-shaped mounts do NOT (the full set still renders
//       below); refs to budget-dropped sets render their stub line.

type WireRenderItem =
  | { kind: "claim"; text: string }
  | { kind: "caveat"; text: string }
  | { kind: "insight"; text: string }
  | { kind: "tiles"; sets: IntelResultSet[] }
  | { kind: "strip"; qtag: string; items: KpiItem[]; insight?: string }
  | {
      kind: "card";
      qtag: string;
      displaySet: IntelResultSet;
      ordinal: number;
      viewNote?: string;
      chartRef?: IntelChartProposal;
      fallbackNote?: string;
      insight?: string;
    }
  | { kind: "stub"; stub: IntelDroppedSetStub }
  | { kind: "missing_ref"; qtag: string };

function planWireBlocks(
  blocks: IntelWireBlock[],
  setByQtag: Map<string, { set: IntelResultSet; ordinal: number }>,
  droppedSets: IntelDroppedSetStub[],
): { items: WireRenderItem[]; consumedQtags: Set<string> } {
  // (a) adjacency: a claim right after a table_ref it cites attaches there.
  const insightByBlockIndex = new Map<number, string>();
  const attachedClaims = new Set<number>();
  blocks.forEach((block, i) => {
    const next = blocks[i + 1];
    if (
      block.kind === "table_ref" &&
      next?.kind === "claim" &&
      next.evidence.includes(block.qtag)
    ) {
      insightByBlockIndex.set(i, next.text);
      attachedClaims.add(i + 1);
    }
  });

  // (b) stable partition, model order preserved within each half.
  const proseIdx: number[] = [];
  const refIdx: number[] = [];
  blocks.forEach((block, i) => {
    if (block.kind === "claim" || block.kind === "caveat") {
      if (!attachedClaims.has(i)) proseIdx.push(i);
    } else {
      refIdx.push(i);
    }
  });

  const items: WireRenderItem[] = [];
  const consumedQtags = new Set<string>();
  const mounted = new Set<string>();
  const stubByQtag = new Map(droppedSets.map((stub) => [stub.qtag, stub]));

  for (const i of proseIdx) {
    const block = blocks[i];
    if (block.kind === "claim") items.push({ kind: "claim", text: block.text });
    else if (block.kind === "caveat") items.push({ kind: "caveat", text: block.text });
  }

  const pushTile = (set: IntelResultSet) => {
    const last = items[items.length - 1];
    if (last?.kind === "tiles") last.sets.push(set);
    else items.push({ kind: "tiles", sets: [set] });
  };

  for (const i of refIdx) {
    const block = blocks[i];
    if (block.kind !== "table_ref" && block.kind !== "chart_ref") continue;
    const insight = block.kind === "table_ref" ? insightByBlockIndex.get(i) : undefined;
    if (mounted.has(block.qtag)) {
      // Repeats render nothing — but a claim attached to the repeat must not
      // vanish (engine-verified text renders SOMEWHERE, never silently drops).
      if (insight !== undefined) items.push({ kind: "claim", text: insight });
      continue;
    }
    mounted.add(block.qtag);
    const match = setByQtag.get(block.qtag);
    if (!match) {
      const stub = stubByQtag.get(block.qtag);
      items.push(stub ? { kind: "stub", stub } : { kind: "missing_ref", qtag: block.qtag });
      // No card to attach to — the claim falls back to its natural chip form.
      if (insight !== undefined) items.push({ kind: "claim", text: insight });
      continue;
    }
    const { set, ordinal } = match;

    if (isOneByOneSet(set)) {
      // 1×1 tile floor beats every block kind, chart fallbacks included.
      pushTile(set);
      consumedQtags.add(block.qtag);
      if (insight !== undefined) items.push({ kind: "insight", text: insight });
      continue;
    }

    if (block.kind === "chart_ref") {
      // Strip floor first (44 R11 reading): no proposal — valid included —
      // may replace the deterministic 1×(2–8) strip; a valid kpi_row on this
      // shape is redundant with the floor anyway.
      const floor = kpiFloorItems(set);
      if (floor) {
        items.push({ kind: "strip", qtag: block.qtag, items: floor });
        consumedQtags.add(block.qtag);
        continue;
      }
      // Client re-validation double-checks the server verdict.
      const verdict = block.proposal != null ? resolveChartRef(set, block.proposal) : null;
      if (verdict?.ok) {
        items.push({ kind: "card", qtag: block.qtag, displaySet: set, ordinal, chartRef: block.proposal ?? undefined });
      } else if (block.proposal === null) {
        items.push({
          kind: "card",
          qtag: block.qtag,
          displaySet: set,
          ordinal,
          fallbackNote: `Couldn't chart this — showing the table (${block.fallback_reason})`,
        });
      } else {
        // Server approved, client rejected: pass the proposal through — the
        // card renders the table plus the existing "Chart unavailable" note.
        items.push({ kind: "card", qtag: block.qtag, displaySet: set, ordinal, chartRef: block.proposal });
      }
      consumedQtags.add(block.qtag);
      continue;
    }

    // table_ref
    if (block.view) {
      items.push({
        kind: "card",
        qtag: block.qtag,
        displaySet: applyTableView(set, block.view),
        ordinal,
        viewNote: block.view_note,
        insight,
      });
      // View-shaped mounts do NOT consume — the full set still renders below.
      continue;
    }
    const floor = kpiFloorItems(set);
    if (floor) {
      items.push({ kind: "strip", qtag: block.qtag, items: floor, insight });
    } else {
      items.push({ kind: "card", qtag: block.qtag, displaySet: set, ordinal, insight });
    }
    consumedQtags.add(block.qtag);
  }

  return { items, consumedQtags };
}

/** Fixed amber callout — caveats and adjacency insights share ONE chrome (46:
 *  `severity` is client-internal and never model-derived; 44 R12: verdict
 *  coloring is out of v1 entirely). */
function WarnCallout({ text }: { text: string }) {
  return <SectionCallout severity="warn" body={text} />;
}

/** One budget-dropped set's honest stub line — shared by the inline block
 *  mount and the audit bottom (the same set may disclose in both places). */
function DroppedStubLine({ stub }: { stub: IntelDroppedSetStub }) {
  return (
    <p className="text-xs text-muted-foreground">
      {stub.label} — {stub.total_row_count} {stub.total_row_count === 1 ? "row" : "rows"}{" "}
      omitted (over the size budget)
    </p>
  );
}

function WireBlockAnswer({
  items,
  multiSet,
  citedCells,
}: {
  items: WireRenderItem[];
  multiSet: boolean;
  citedCells: (qtag: string) => number | null;
}) {
  return (
    <>
      {items.map((item, i) => {
        switch (item.kind) {
          case "claim":
            return (
              <RevealBlock key={i}>
                <AnswerChip body={item.text} />
              </RevealBlock>
            );
          case "caveat":
          case "insight":
            return (
              <RevealBlock key={i}>
                <WarnCallout text={item.text} />
              </RevealBlock>
            );
          case "tiles":
            return (
              <RevealBlock key={i}>
                <KpiStrip sets={item.sets} />
              </RevealBlock>
            );
          case "strip":
            return (
              <RevealBlock key={i}>
                <KpiFloorStrip items={item.items} />
                {item.insight !== undefined ? (
                  <div className="mt-2">
                    <WarnCallout text={item.insight} />
                  </div>
                ) : null}
              </RevealBlock>
            );
          case "card":
            return (
              <RevealBlock key={i}>
                <ResultSetCard
                  set={item.displaySet}
                  ordinal={item.ordinal}
                  multiSet={multiSet}
                  citedCells={citedCells(item.qtag)}
                  defaultOpen
                  chartRef={item.chartRef}
                  viewNote={item.viewNote}
                  fallbackNote={item.fallbackNote}
                />
                {item.insight !== undefined ? (
                  <div className="mt-2">
                    <WarnCallout text={item.insight} />
                  </div>
                ) : null}
              </RevealBlock>
            );
          case "stub":
            return (
              <RevealBlock key={i}>
                <DroppedStubLine stub={item.stub} />
              </RevealBlock>
            );
          case "missing_ref":
            return (
              <RevealBlock key={i}>
                <p className="text-xs italic text-muted-foreground">
                  (table unavailable in this response)
                </p>
              </RevealBlock>
            );
        }
      })}
    </>
  );
}

function AssistantTurn({ turn }: { turn: IntelTurn }) {
  const setByQtag = new Map(
    turn.resultSets.map((set, i) => [set.qtag, { set, ordinal: i + 1 }]),
  );
  const multiSet = turn.resultSets.length > 1;
  const citedCells = (qtag: string) =>
    (turn.finalMeta?.provenance_sets ?? []).find((p) => p.qtag === qtag)?.cited_cells ?? null;

  // Contract v2 (ticket 38, Option A — dedicated block-render path): when the
  // answer_blocks frame arrived and the run isn't a backstop, render from the
  // wire blocks and ignore the text frame (it duplicates the blocks as
  // progressive enhancement). Backstop runs and v1 wires keep the text path —
  // the engine line + [[table:qtag]] markers render "engine line + full
  // labelled stack" through SectionedAnswer.
  const blockPath = turn.answerBlocks != null && turn.diagnostics?.assembly !== "backstop";

  let answerRegion: ReactNode = null;
  let inlineQtags: Set<string>;
  if (blockPath) {
    const plan = planWireBlocks(turn.answerBlocks ?? [], setByQtag, turn.droppedSets);
    inlineQtags = plan.consumedQtags;
    answerRegion = (
      <WireBlockAnswer items={plan.items} multiSet={multiSet} citedCells={citedCells} />
    );
  } else {
    // Ticket 31: split the buffered answer on table-ref markers; a marker
    // whose qtag has a shipped set mounts that set INLINE (card, or KPI tile
    // when 1×1) and the Supporting-data stack skips it so each set renders
    // exactly once.
    const answerChunks = splitAnswerOnTableRefs(turn.answer);
    inlineQtags = new Set(
      answerChunks.flatMap((c) =>
        c.kind === "table" && setByQtag.has(c.qtag) ? [c.qtag] : [],
      ),
    );
    answerRegion = turn.answer ? (
      <SectionedAnswer
        answer={turn.answer}
        setByQtag={setByQtag}
        multiSet={multiSet}
        citedCells={citedCells}
      />
    ) : null;
  }

  const stackSets = turn.resultSets
    .map((set, i) => ({ set, ordinal: i + 1 }))
    .filter(({ set }) => !inlineQtags.has(set.qtag));
  // Work/debug sets (contract-v2 role when present, else the v1 `internal`
  // bridge; server-guarded: cited never arrives work/debug, all-internal
  // never arrives) soft-hide behind one collapsed "N working queries" line.
  const workingSets = stackSets.filter(({ set }) => isWorkSet(set));
  const shownSets = stackSets.filter(({ set }) => !isWorkSet(set));
  // Emit-all expand rule: cited sets open, uncited collapsed; when nothing is
  // cited (backstop run) the first 2 open.
  const anyCited = shownSets.some(({ set }) => isCitedSet(set));
  // Floors over the stack (R5 + ticket 45 fold-in): consecutive 1×1 sets
  // coalesce into one KPI strip; a 1×(2–8) all-numeric set renders as a bare
  // strip; everything else keeps its ResultSetCard (index preserved for the
  // first-2-open backstop rule).
  type StackGroup =
    | { kind: "tiles"; sets: IntelResultSet[] }
    | { kind: "strip"; qtag: string; items: KpiItem[] }
    | { kind: "card"; set: IntelResultSet; ordinal: number; index: number };
  const stackGroups: StackGroup[] = [];
  shownSets.forEach(({ set, ordinal }, index) => {
    const last = stackGroups[stackGroups.length - 1];
    if (isOneByOneSet(set)) {
      if (last?.kind === "tiles") last.sets.push(set);
      else stackGroups.push({ kind: "tiles", sets: [set] });
      return;
    }
    const floor = kpiFloorItems(set);
    if (floor) {
      stackGroups.push({ kind: "strip", qtag: set.qtag, items: floor });
      return;
    }
    stackGroups.push({ kind: "card", set, ordinal, index });
  });
  return (
    // Staggered-entrance parent (DashboardRenderer pattern): RevealBlock
    // children inherit the show orchestration so sibling blocks cascade in.
    <motion.div
      className="flex flex-col gap-3"
      initial="hidden"
      animate="show"
      variants={{ show: { transition: { staggerChildren: SECTION_STAGGER } } }}
    >
      {answerRegion}

      {turn.diagnostics?.assembly === "partial" ? (
        // Engine-authored fixed copy — degraded_reason wording is internal
        // diagnostics, not user prose (judgment call for the 24 sweep).
        <p className="text-xs italic text-muted-foreground">
          Part of this answer failed verification and was omitted.
        </p>
      ) : null}

      {stackGroups.map((group, gi) =>
        group.kind === "tiles" ? (
          <RevealBlock key={`tiles_${gi}`}>
            <KpiStrip sets={group.sets} />
          </RevealBlock>
        ) : group.kind === "strip" ? (
          <RevealBlock key={group.qtag || `strip_${gi}`}>
            <KpiFloorStrip items={group.items} />
          </RevealBlock>
        ) : (
          <RevealBlock key={group.set.qtag || group.ordinal}>
            <ResultSetCard
              set={group.set}
              ordinal={group.ordinal}
              multiSet={multiSet}
              citedCells={citedCells(group.set.qtag)}
              defaultOpen={anyCited ? isCitedSet(group.set) : group.index < 2}
            />
          </RevealBlock>
        ),
      )}

      {workingSets.length > 0 ? (
        <WorkingQueriesGroup sets={workingSets} multiSet={multiSet} />
      ) : null}

      {turn.droppedSets.length > 0 ? (
        // Contract v2: one honest stub line per budget-dropped set replaces
        // the scalar drop line (which stays for v1 wires).
        turn.droppedSets.map((stub) => <DroppedStubLine key={stub.qtag} stub={stub} />)
      ) : turn.setsDropped > 0 ? (
        <p className="text-xs text-muted-foreground">
          {turn.setsDropped} more result {turn.setsDropped === 1 ? "set" : "sets"} omitted
          (over the size budget)
        </p>
      ) : null}

      {turn.status === "error" && turn.errorMessage ? (
        <SectionCallout severity="risk" body={turn.errorMessage} />
      ) : null}

      {/* Bouncing "writing" dots while content is still arriving; unmounts
          the moment the run settles. */}
      {turn.status === "running" ? <StreamingDots /> : null}
    </motion.div>
  );
}

// ---- Ruling 40 R4a: the section-chips answer transform ---------------------
// The buffered final answer parses into typed blocks, groups into sections at
// headings, and within each section prose stable-partitions ABOVE its tables.
// Paragraphs render as boxed chips; gfm tables and code fences render as plain
// markdown; table-ref markers keep the ticket-31 semantics (inline mount,
// stack dedup, repeat → nothing, unknown qtag → muted one-liner) with the R5
// tile floor applied.

type AnswerBlock =
  | { kind: "heading"; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "gfm_table"; text: string }
  | { kind: "code_fence"; text: string }
  | { kind: "table_ref"; qtag: string };

function parseAnswerBlocks(answer: string): AnswerBlock[] {
  const blocks: AnswerBlock[] = [];
  for (const chunk of splitAnswerOnTableRefs(answer)) {
    if (chunk.kind === "table") {
      blocks.push({ kind: "table_ref", qtag: chunk.qtag });
      continue;
    }
    const lines = chunk.text.split("\n");
    let para: string[] = [];
    const flushPara = () => {
      if (para.length > 0) {
        blocks.push({ kind: "paragraph", text: para.join("\n") });
        para = [];
      }
    };
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (/^\s*$/.test(line)) {
        flushPara();
        i += 1;
      } else if (/^\s*```/.test(line)) {
        flushPara();
        const fence = [line];
        i += 1;
        while (i < lines.length && !/^\s*```/.test(lines[i])) {
          fence.push(lines[i]);
          i += 1;
        }
        if (i < lines.length) {
          fence.push(lines[i]);
          i += 1;
        }
        blocks.push({ kind: "code_fence", text: fence.join("\n") });
      } else if (/^#{1,6}\s/.test(line)) {
        flushPara();
        blocks.push({ kind: "heading", text: line.replace(/^#{1,6}\s+/, "").trim() });
        i += 1;
      } else if (/^\s*\*\*[^*]+\*\*:?\s*$/.test(line)) {
        // A line that is entirely one bold span ("**Answered survey rate:**",
        // colon inside or outside the bold) is a model pseudo-heading — treat
        // it as a real heading so the title and its section share one card
        // instead of the title landing in its own paragraph chip.
        flushPara();
        const text = line
          .trim()
          .replace(/^\*\*/, "")
          .replace(/\*\*:?$/, "")
          .replace(/:$/, "")
          .trim();
        blocks.push({ kind: "heading", text });
        i += 1;
      } else if (/^\s*\|/.test(line)) {
        flushPara();
        const table: string[] = [];
        while (i < lines.length && /^\s*\|/.test(lines[i])) {
          table.push(lines[i]);
          i += 1;
        }
        blocks.push({ kind: "gfm_table", text: table.join("\n") });
      } else {
        // Contiguous other non-blank lines — a markdown list counts as one
        // paragraph block. Model "•" glyphs normalize to markdown "- " items
        // first (presentation normalization only — ReactMarkdown renders soft
        // line breaks as spaces, so raw "•" bullets run together).
        para.push(...normalizeBulletGlyphs(line));
        i += 1;
      }
    }
    flushPara();
  }
  return blocks;
}

/** Presentation normalization of model bullet glyphs: a "• item" line becomes
 *  a markdown "- item" line, and one run-on line whose items are joined by
 *  internal " • " separators (starting with "•" or carrying ≥2 separators)
 *  splits into one "- " line per item. */
function normalizeBulletGlyphs(line: string): string[] {
  const separators = (line.match(/\s•\s/g) ?? []).length;
  if (separators >= 1 && (/^\s*•/.test(line) || separators >= 2)) {
    // Leading text before the first separator ("Notable gaps: • A • B") is
    // intro prose, not a bullet item — keep it as a plain line.
    const startsWithBullet = /^\s*•/.test(line);
    return line
      .split(/\s+•\s+/)
      .map((part) => part.replace(/^\s*•\s*/, "").trim())
      .filter(Boolean)
      .map((part, idx) => (idx === 0 && !startsWithBullet ? part : `- ${part}`));
  }
  if (/^\s*•\s+/.test(line)) return [line.replace(/^\s*•\s+/, "- ")];
  return [line];
}

type AnswerSectionGroup = { heading: string | null; blocks: AnswerBlock[] };

/** Sections split at headings; content before the first heading is the lead
 *  section (no heading). */
function groupAnswerSections(blocks: AnswerBlock[]): AnswerSectionGroup[] {
  const sections: AnswerSectionGroup[] = [];
  let current: AnswerSectionGroup = { heading: null, blocks: [] };
  const push = () => {
    if (current.heading !== null || current.blocks.length > 0) sections.push(current);
  };
  for (const block of blocks) {
    if (block.kind === "heading") {
      push();
      current = { heading: block.text, blocks: [] };
    } else {
      current.blocks.push(block);
    }
  }
  push();
  return sections;
}

/** Chip/section-card interior typography (ticket 45): AnswerSection's own
 *  .prose margins (p my-3, section pb-2) stack lopsidedly inside the py-3 chip
 *  padding — neutralize them and let the container space blocks; body text
 *  drops to text-sm (titles stay text-foreground). Scoped to these containers
 *  only — ResultSetCard and other surfaces are untouched. */
const CHIP_PROSE_CLASSES =
  "space-y-2 [&_p]:my-0 [&_section]:pb-0 [&_.prose]:text-sm [&_.prose]:leading-6 [&_.prose]:text-foreground/90";

/** One paragraph-style answer chip (FollowupChips hover language minus the
 *  scale — 1.02 reads as wobble on a full-width chip). Shared by the markdown
 *  text path and the contract-v2 claim blocks. */
function AnswerChip({ body }: { body: string }) {
  return (
    <div
      data-testid="answer-chip"
      className={cn(
        "rounded-lg border border-border bg-card/40 px-4 py-3 transition-all duration-200 ease-out hover:shadow-md motion-safe:hover:-translate-y-0.5",
        CHIP_PROSE_CLASSES,
      )}
    >
      <AnswerSection body={body} />
    </div>
  );
}

function SectionedAnswer({
  answer,
  setByQtag,
  multiSet,
  citedCells,
}: {
  answer: string;
  setByQtag: Map<string, { set: IntelResultSet; ordinal: number }>;
  multiSet: boolean;
  citedCells: (qtag: string) => number | null;
}) {
  const sections = groupAnswerSections(parseAnswerBlocks(answer));
  // Shared across sections so a repeat marker anywhere in the answer renders
  // nothing (ticket 31 contract).
  const mountedQtags = new Set<string>();
  const isTableLike = (b: AnswerBlock) => b.kind === "gfm_table" || b.kind === "table_ref";
  return (
    <>
      {sections.map((section, si) => {
        // Stable partition: prose blocks first in original order, then
        // table-like blocks in original order.
        const prose = section.blocks.filter((b) => !isTableLike(b));
        const tableLikes = section.blocks.filter(isTableLike);
        const shared = { setByQtag, multiSet, citedCells, mountedQtags };
        if (section.heading === null) {
          // Lead section: per-paragraph chips.
          return (
            <Fragment key={si}>
              {renderAnswerBlocks({
                blocks: [...prose, ...tableLikes],
                keyPrefix: `s${si}`,
                ...shared,
              })}
            </Fragment>
          );
        }
        // Headed section: ONE bordered card carries the heading as its title
        // plus the section's prose (no nested chip borders); table-likes and
        // KPI strips render after/outside the card as before.
        return (
          <Fragment key={si}>
            <RevealBlock>
              <div
                data-testid="answer-section-card"
                // Hover treatment is shadow-only here (judgment call): the
                // FollowupChips lift+scale reads as wobble on a full-width card.
                className="rounded-lg border border-border bg-card/40 px-4 py-3 transition-shadow duration-200 ease-out hover:shadow-md"
              >
                <h3
                  className={cn(
                    "text-sm font-semibold text-foreground",
                    prose.length > 0 && "mb-2",
                  )}
                >
                  {section.heading}
                </h3>
                {prose.length > 0 ? (
                  <div className={CHIP_PROSE_CLASSES}>
                    {prose.map((block, bi) => (
                      <AnswerSection key={bi} body={block.text} />
                    ))}
                  </div>
                ) : null}
              </div>
            </RevealBlock>
            {renderAnswerBlocks({
              blocks: tableLikes,
              keyPrefix: `s${si}`,
              ...shared,
            })}
          </Fragment>
        );
      })}
    </>
  );
}

function renderAnswerBlocks({
  blocks,
  keyPrefix,
  setByQtag,
  multiSet,
  citedCells,
  mountedQtags,
}: {
  blocks: AnswerBlock[];
  keyPrefix: string;
  setByQtag: Map<string, { set: IntelResultSet; ordinal: number }>;
  multiSet: boolean;
  citedCells: (qtag: string) => number | null;
  mountedQtags: Set<string>;
}): ReactNode[] {
  const out: ReactNode[] = [];
  // R5 coalescing in the answer flow: consecutive 1×1 table-ref mounts merge
  // into one KPI strip.
  let tileRun: IntelResultSet[] = [];
  const flushTiles = () => {
    if (tileRun.length > 0) {
      out.push(
        <RevealBlock key={`${keyPrefix}_tiles_${out.length}`}>
          <KpiStrip sets={tileRun} />
        </RevealBlock>,
      );
      tileRun = [];
    }
  };
  blocks.forEach((block, i) => {
    const key = `${keyPrefix}_${i}`;
    if (block.kind === "table_ref") {
      const match = setByQtag.get(block.qtag);
      if (!match) {
        flushTiles();
        out.push(
          <RevealBlock key={key}>
            <p className="text-xs italic text-muted-foreground">
              (table unavailable in this response)
            </p>
          </RevealBlock>,
        );
        return;
      }
      // A repeat marker renders nothing (and doesn't break a tile run).
      if (mountedQtags.has(block.qtag)) return;
      mountedQtags.add(block.qtag);
      if (isOneByOneSet(match.set)) {
        tileRun.push(match.set);
        return;
      }
      const floorItems = kpiFloorItems(match.set);
      if (floorItems) {
        // Ticket 45 fold-in: the 1×(2–8) strip floor applies to inline
        // mounts on the text path too.
        flushTiles();
        out.push(
          <RevealBlock key={key}>
            <KpiFloorStrip items={floorItems} />
          </RevealBlock>,
        );
        return;
      }
      flushTiles();
      out.push(
        <RevealBlock key={key}>
          <ResultSetCard
            set={match.set}
            ordinal={match.ordinal}
            multiSet={multiSet}
            citedCells={citedCells(match.set.qtag)}
            defaultOpen
          />
        </RevealBlock>,
      );
      return;
    }
    flushTiles();
    if (block.kind === "paragraph") {
      out.push(
        <RevealBlock key={key}>
          <AnswerChip body={block.text} />
        </RevealBlock>,
      );
      return;
    }
    // Gfm tables (prose table CSS handles them) and code fences (never
    // chipped) render as plain answer markdown.
    out.push(
      <RevealBlock key={key}>
        <AnswerSection body={block.text} />
      </RevealBlock>,
    );
  });
  flushTiles();
  return out;
}

/** The soft-hide for internal sets: one collapsed muted line; expanding
 *  reveals the same cards, each starting collapsed. */
function WorkingQueriesGroup({
  sets,
  multiSet,
}: {
  sets: { set: IntelResultSet; ordinal: number }[];
  multiSet: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="group flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
        {sets.length} working {sets.length === 1 ? "query" : "queries"}
        <ChevronDown
          className={cn(
            "h-3 w-3 shrink-0 transition-transform duration-200",
            open ? "rotate-0" : "-rotate-90",
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 flex flex-col gap-3">
          {sets.map(({ set, ordinal }) => (
            <ResultSetCard
              key={set.qtag || ordinal}
              set={set}
              ordinal={ordinal}
              multiSet={multiSet}
              citedCells={null}
              defaultOpen={false}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
